import React from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Box, Typography, Button, Grid, TextField, Divider } from '@mui/material'
import headerBgImage from '../../assets/images/arbol-ec/fondos/019.jpg'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { MAIN } from '../../config/routes'
import UNLMap from '../../components/UNLMap'
import { postContactForm } from '../../services'
import { AlertContext } from '../../context/AlertContext'

const ContactSection = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { alertRef } = React.useContext(AlertContext)
  const handleOnClick = () => navigate(MAIN)

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await postContactForm(values)
      if (response.success) {
        alertRef?.current?.message('Mensaje enviado con éxito')
        alertRef?.current?.isSeverity('success')
        alertRef?.current?.openAlert(true)
        resetForm()
      } else {
        alertRef?.current?.message('Error al enviar el mensaje')
        alertRef?.current?.isSeverity('error')
        alertRef?.current?.openAlert(true)
      }
    } catch (error) {
      console.error('Error al enviar el mensaje:', error)
      alertRef?.current?.message('Ocurrió un error al enviar el mensaje')
      alertRef?.current?.isSeverity('error')
      alertRef?.current?.openAlert(true)
    } finally {
      setSubmitting(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      nombre: '',
      correo: '',
      asunto: '',
      mensaje: '',
    },
    validationSchema: Yup.object({
      nombre: Yup.string().required('El nombre es obligatorio'),
      correo: Yup.string().email('Correo inválido').required('El correo es obligatorio'),
      asunto: Yup.string().required('El asunto es obligatorio'),
      mensaje: Yup.string().required('El mensaje es obligatorio'),
    }),
    onSubmit: handleSubmit,
  })

  return (
    <Box sx={{ fontFamily: theme.typography.fontFamily }}>
      <Box
        sx={{
          backgroundImage: `url(${headerBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          position: 'relative',
          color: theme.palette.text.ivory,
          mt: '5.8vh',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `${theme.palette.text.darkBlue}AA`,
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 4, md: 5 } }}>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              color: theme.palette.text.white,
              fontWeight: 600,
              fontSize: '1.2rem',
              textShadow: '0 0 1px rgba(255, 255, 255, 0.8)',
            }}>
            Tu opinión nutre este proyecto. ¡Déjanos tus ideas o preguntas!
          </Typography>
        </Box>
      </Box>

      <Box pb={4} pt={{ xs: 1, md: 2 }} px={{ xs: 5, md: 8, lg: 10 }}>
        <Box component="nav" mt={3}>
          <Box
            component="ol"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              listStyle: 'none',
              justifyContent: 'end',
              px: 0,
              m: 0,
              fontSize: 16,
              fontWeight: 400,
            }}>
            <Box component="li">
              <Button
                onClick={handleOnClick}
                variant="text"
                sx={theme => ({
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.palette.text.mustard,
                  py: 0,
                  px: 2,
                  minWidth: 0,
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                })}>
                Inicio
              </Button>
            </Box>
            <Box
              component="li"
              sx={{
                pl: 1,
                position: 'relative',
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}>
              <Box component="span" sx={{ pr: 1 }}>
                /
              </Box>{' '}
              Contacto
            </Box>
          </Box>
        </Box>

        <Divider />
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            pl: 4,
          }}>
          <Typography
            variant="h2"
            gutterBottom
            fontWeight={600}
            sx={{ color: theme.palette.text.forestGreen, my: 2 }}>
            Contáctenos
          </Typography>
        </Box>
      </Box>

      {/* Mapa y formulario */}
      <Box pb={6} px={{ xs: 5, md: 8, lg: 10 }}>
        <Grid container spacing={4} alignItems="flex-start" justifyContent="center">
          <Grid item xs={12} sm={10} md={6} lg={9}>
            <UNLMap />
          </Grid>

          <Grid item xs={12} sm={10} md={6} lg={3}>
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
              <Typography variant="h6" mb={1} color={theme.palette.text.primary}>
                Envíar un mensaje
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="nombre"
                    name="nombre"
                    label="Nombre"
                    value={formik.values.nombre}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                    helperText={formik.touched.nombre && formik.errors.nombre}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="correo"
                    name="correo"
                    label="Correo electrónico"
                    value={formik.values.correo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.correo && Boolean(formik.errors.correo)}
                    helperText={formik.touched.correo && formik.errors.correo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="asunto"
                    name="asunto"
                    label="Asunto"
                    value={formik.values.asunto}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.asunto && Boolean(formik.errors.asunto)}
                    helperText={formik.touched.asunto && formik.errors.asunto}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    id="mensaje"
                    name="mensaje"
                    label="Mensaje"
                    value={formik.values.mensaje}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.mensaje && Boolean(formik.errors.mensaje)}
                    helperText={formik.touched.mensaje && formik.errors.mensaje}
                  />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ bgcolor: theme.palette.primary.main, width: 150 }}>
                    Enviar
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default ContactSection
